import { signInWithCustomToken } from 'firebase/auth'
import { auth } from './firebase'

// LINE authentication types
interface LineAuthResponse {
  success: boolean
  firebaseToken?: string
  lineAccessToken?: string
  user?: {
    id: string
    name: string
    email: string | null
    picture: string
    provider: string
  }
  error?: string
}


interface LineLoginOptions {
  redirect_uri?: string
}

/**
 * LINE Login service for Firebase authentication integration
 * Provides methods to initiate LINE login and handle authentication flow
 */
export class LineAuthService {
  private static instance: LineAuthService
  private baseUrl: string

  private constructor() {
    // Use external LINE authentication service
    this.baseUrl = 'https://auth.firebase.dataslot.cc'
  }

  public static getInstance(): LineAuthService {
    if (!LineAuthService.instance) {
      LineAuthService.instance = new LineAuthService()
    }
    return LineAuthService.instance
  }

  /**
   * Initiates LINE Login by redirecting to LINE OAuth
   * @param options Optional redirect URI for custom callback handling
   */
  public async initiateLineLogin(options?: LineLoginOptions): Promise<void> {
    try {
      const params = new URLSearchParams()
      
      // Set callback URL to local application callback route
      const callbackUrl = options?.redirect_uri || `${window.location.origin}/auth/line/callback`
      params.set('redirect_uri', callbackUrl)
      
      const loginUrl = `${this.baseUrl}/auth/line/login?${params.toString()}`
      
      console.log('[LineAuth] Redirecting to external LINE login service:', loginUrl)
      window.location.href = loginUrl
    } catch (error) {
      console.error('[LineAuth] Failed to initiate LINE login:', error)
      throw new Error('Failed to start LINE login process')
    }
  }

  /**
   * Handles LINE authentication callback and signs into Firebase
   * @param callbackUrl The callback URL containing auth response
   * @returns Promise resolving to authentication result
   */
  public async handleLineCallback(callbackUrl: string): Promise<LineAuthResponse> {
    try {
      console.log('[LineAuth] Handling callback:', callbackUrl)
      
      // Parse URL parameters from the callback
      const url = new URL(callbackUrl)
      const params = new URLSearchParams(url.search)
      
      // Check for error parameters
      const error = params.get('error')
      const errorDescription = params.get('error_description')
      
      if (error) {
        throw new Error(errorDescription || error)
      }
      
      // Get the Firebase token from URL parameters
      const firebaseToken = params.get('token')
      console.log('[LineAuth] Callback URL params:', Object.fromEntries(params.entries()))
      console.log('[LineAuth] Received Firebase token:', firebaseToken ? 'Present' : 'Missing')
      
      if (!firebaseToken) {
        throw new Error('No Firebase token received from LINE authentication')
      }
      
      // Log token details for debugging (first/last few characters only)
      console.log('[LineAuth] Token format check:', {
        length: firebaseToken.length,
        prefix: firebaseToken.substring(0, 10),
        suffix: firebaseToken.substring(firebaseToken.length - 10)
      })
      
      // Sign into Firebase with custom token
      console.log('[LineAuth] Attempting Firebase signInWithCustomToken...')
      
      // Add a small delay to ensure Firebase is ready
      await new Promise(resolve => setTimeout(resolve, 100))
      
      try {
        const userCredential = await signInWithCustomToken(auth, firebaseToken)
        console.log('[LineAuth] Firebase signInWithCustomToken successful:', {
          user: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName
        })
      } catch (firebaseError: any) {
        console.error('[LineAuth] Firebase signInWithCustomToken failed:', {
          code: firebaseError?.code,
          message: firebaseError?.message,
          details: firebaseError
        })
        
        // Handle invalid custom token error with actionable guidance
        if (firebaseError?.code === 'auth/invalid-custom-token') {
          console.error('[LineAuth] SOLUTION REQUIRED: The external LINE authentication service needs configuration update')
          console.error('Expected Firebase project: dataslot-393113')
          console.error('Service at: https://auth.firebase.dataslot.cc')
          
          // Decode and compare token details
          try {
            const payload = JSON.parse(atob(firebaseToken.split('.')[1]))
            const expectedIssuer = 'firebase-adminsdk-*@dataslot-393113.iam.gserviceaccount.com'
            const expectedAudience = 'https://identitytoolkit.googleapis.com/v1/projects/dataslot-393113'
            
            console.error('Token Analysis:', {
              actualIssuer: payload.iss,
              expectedIssuer,
              actualAudience: payload.aud,
              expectedAudience,
              projectMatch: payload.aud?.includes('dataslot-393113'),
              tokenExpiry: new Date(payload.exp * 1000).toISOString(),
              tokenIssuedAt: new Date(payload.iat * 1000).toISOString()
            })
            
            if (!payload.aud?.includes('dataslot-393113')) {
              console.error('❌ CONFIGURATION MISMATCH: External service is configured for different Firebase project')
              console.error('Contact service administrator to update Firebase project configuration')
            }
          } catch (e) {
            console.error('Could not decode token payload:', e)
          }
          
          // Throw user-friendly error
          throw new Error(
            'LINE Login service configuration mismatch. ' +
            'The external authentication service needs to be updated for this Firebase project. ' +
            'Please contact support with error code: AUTH_PROJECT_MISMATCH'
          )
        }
        
        throw firebaseError
      }
      
      console.log('[LineAuth] Successfully authenticated with Firebase')
      
      // Return success response
      const response: LineAuthResponse = {
        success: true,
        firebaseToken,
        user: {
          id: 'line-user', // Will be updated by Firebase auth state
          name: 'LINE User',
          email: null,
          picture: '',
          provider: 'line'
        }
      }
      
      return response
    } catch (error: any) {
      console.error('[LineAuth] Callback handling failed:', {
        error: error,
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      })
      
      // Provide more specific error messages
      if (error?.code === 'auth/invalid-custom-token') {
        console.error('[LineAuth] Invalid custom token - possible causes:')
        console.error('1. Token was created for different Firebase project')
        console.error('2. Token format is invalid')
        console.error('3. Token has expired')
        console.error('4. Firebase project settings don\'t allow custom tokens')
      }
      
      throw error
    }
  }

  /**
   * Signs in with LINE using custom token flow
   * This method handles the complete LINE -> Firebase authentication flow
   * @param firebaseToken Custom Firebase token from LINE authentication
   */
  public async signInWithLineToken(firebaseToken: string): Promise<void> {
    try {
      await signInWithCustomToken(auth, firebaseToken)
      console.log('[LineAuth] Firebase sign-in successful')
    } catch (error) {
      console.error('[LineAuth] Firebase sign-in failed:', error)
      throw new Error('Failed to sign in with Firebase')
    }
  }

}

// Convenience functions for direct usage
export const lineAuth = LineAuthService.getInstance()

/**
 * Helper function to start LINE login process
 * @param options Optional configuration for LINE login
 */
export const startLineLogin = (options?: LineLoginOptions) => {
  return lineAuth.initiateLineLogin(options)
}

/**
 * Helper function to handle LINE login callback
 * @param callbackUrl The callback URL from LINE OAuth
 */
export const handleLineLoginCallback = (callbackUrl: string) => {
  return lineAuth.handleLineCallback(callbackUrl)
}

/**
 * Helper function to sign in with LINE custom token
 * @param firebaseToken Custom Firebase token from LINE auth
 */
export const signInWithLineToken = (firebaseToken: string) => {
  return lineAuth.signInWithLineToken(firebaseToken)
}

/**
 * Error handling utility for LINE authentication
 */
export const getLineAuthErrorMessage = (error: any): string => {
  if (error?.message) {
    return error.message
  }
  
  // Handle common LINE auth errors
  if (typeof error === 'string') {
    switch (error) {
      case 'access_denied':
        return 'LINE login was cancelled. Please try again.'
      case 'invalid_request':
        return 'Invalid LINE login request. Please try again.'
      case 'unauthorized_client':
        return 'LINE login is not properly configured. Please contact support.'
      case 'invalid_grant':
        return 'LINE authentication expired. Please try again.'
      default:
        return error
    }
  }
  
  return 'An error occurred during LINE login. Please try again.'
}